import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches a render crash in one page and shows a recoverable card instead of
 * a blank white screen — React has no hook-based equivalent, so this stays a
 * class component. Mounted around `<Outlet/>` in `AppShell.tsx`, keyed on the
 * route path, so navigating away from a crashed page and back always gets a
 * fresh mount rather than staying stuck showing the old error.
 *
 * No error-reporting service wired up yet (console only) — see
 * FRONTEND-ROADMAP.md Phase 6, "frontend error tracking (Sentry)". This is
 * the seam that work plugs into later: swap the `console.error` in
 * `componentDidCatch` for a report call, nothing else changes.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled error rendering a page:', error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="grid min-h-[60vh] place-items-center">
          <Card className="w-full max-w-sm">
            <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
              <span className="grid size-12 place-items-center rounded-full bg-danger-muted text-danger ring-1 ring-danger/20">
                <TriangleAlert className="size-6" aria-hidden />
              </span>
              <div>
                <p className="font-medium">Something went wrong</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  This page hit an unexpected error. Reloading usually fixes it.
                </p>
              </div>
              <Button onClick={() => window.location.reload()}>Reload page</Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
